import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10" data-testid="about-page">
      <header>
        <div className="chip chip-green">📖 About</div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-slate-900">About the PM POSHAN MDM Calculator</h1>
        <p className="mt-4 text-slate-600 text-lg leading-relaxed">
          The <b>PM POSHAN MDM Calculator</b> is a free, mobile-friendly tool built to help school
          teachers, Headmasters, MDM In-charges and CRCCs work out the exact daily requirement of a
          Mid Day Meal under the <b>PM POSHAN (Pradhan Mantri Poshan Shakti Nirman)</b> scheme.
          Instead of opening a register and doing the arithmetic by hand every morning, you simply
          enter the number of students present in each category and the tool instantly returns the
          cooking cost, the quantity of every ingredient, and a clean class-wise breakdown that you
          can export.
        </p>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <h2 className="font-display text-2xl font-bold text-slate-900">What is PM POSHAN?</h2>
        <p className="mt-3 text-slate-600 leading-relaxed">
          PM POSHAN (formerly the Mid Day Meal Scheme) is one of the largest school-feeding programmes
          in the world. Under the scheme, every eligible child from Balvatika up to Class VIII — and in
          many states up to Class X — is entitled to a hot cooked meal on every school day. Because
          the per-child cooking cost and the quantity of rice, dal, vegetables, oil and condiments are
          fixed by scheme norms, even a small counting mistake can cause a mismatch between stock
          consumed and stock reported. This calculator removes that risk by applying the correct rate
          for the date you choose and following the day-wise menu automatically.
        </p>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Everything runs inside your browser. No login is required, your numbers are never sent to a
          server, and the tool works smoothly on a low-cost Android phone, a tablet, or a desktop.
        </p>
      </section>

      <section className="rounded-2xl bg-emerald-50 border border-emerald-100 p-6 sm:p-8">
        <h2 className="font-display text-2xl font-bold text-emerald-900">🧭 How to use the calculator</h2>
        <ol className="mt-4 space-y-3 text-emerald-900/90 list-decimal list-inside">
          <li><b>Pick the date.</b> The tool reads the weekday and shows the day’s menu, including whether a nutrition-rich laddoo is served.</li>
          <li><b>Enter present students.</b> Use the plus/minus steppers (or type directly) to set counts for Balvatika, Primary, Upper Primary and High School. Only count children who actually ate.</li>
          <li><b>Generate the report.</b> The tool produces a class-wise table for each category showing the quantity and cost of every ingredient.</li>
          <li><b>Review the totals.</b> Scroll to the summary to see the grand total cooking cost, the average cost per student, and the total rice required for the day.</li>
          <li><b>Export or record.</b> Use the PDF or Excel buttons to save a clean copy for your MDM register, monthly report, or SMS entry.</li>
        </ol>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-display font-bold text-lg text-slate-900">🍚 Per-child rice quantity</h3>
          <ul className="mt-4 space-y-2 text-sm text-slate-700">
            <li className="flex items-center justify-between border-b border-slate-100 pb-2"><span>Balvatika &amp; Primary (I–V)</span><b>100 g</b></li>
            <li className="flex items-center justify-between border-b border-slate-100 pb-2"><span>Upper Primary (VI–VIII)</span><b>150 g</b></li>
            <li className="flex items-center justify-between"><span>High School (IX–X)</span><b>150 g</b></li>
          </ul>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-display font-bold text-lg text-slate-900">💰 Cooking cost components (May 2025+)</h3>
          <ul className="mt-4 space-y-2 text-sm text-slate-700">
            <li className="flex items-center justify-between border-b border-slate-100 pb-2"><span>Balvatika / Primary target</span><b>₹ 11.15 / child</b></li>
            <li className="flex items-center justify-between border-b border-slate-100 pb-2"><span>Upper Primary / High School target</span><b>₹ 14.74 / child</b></li>
            <li className="flex items-center justify-between border-b border-slate-100 pb-2"><span>Laddoo cost (Tue / Thu / Sat)</span><b>₹ 3.50 / piece</b></li>
            <li className="flex items-center justify-between"><span>Vegetables (auto-balance)</span><b>⚖ dynamic</b></li>
          </ul>
        </div>
      </section>

      <div className="text-center">
        <Button asChild size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="about-cta-start">
          <Link to="/calculator">Open calculator <ArrowRight size={16}/></Link>
        </Button>
      </div>
    </div>
  );
}
